import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import Button from './Button';

const Navigation = ({ currentLanguage }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  const content = {
    EN: {
      brand: "PSPXpress",
      nav: {
        home: "Home",
        howItWorks: "How It Works", 
        cardBenefits: "Card & Benefits",
        faq: "FAQ",
        apply: "Apply"
      },
      applyNow: "Apply Now"
    },
    FR: {
      brand: "PSPXpress",
      nav: {
        home: "Accueil",
        howItWorks: "Comment ça marche",
        cardBenefits: "Carte et avantages", 
        faq: "FAQ",
        apply: "Appliquer"
      },
      applyNow: "Faire une demande"
    }
  };

  const text = content?.[currentLanguage] || content?.EN;

  const navigationItems = [
    { path: '/', label: text?.nav?.home },
    { path: '/how-it-works', label: text?.nav?.howItWorks },
    { path: '/card-benefits', label: text?.nav?.cardBenefits },
    { path: '/faq', label: text?.nav?.faq }
  ];

  // Enhanced scroll handling with progress tracking
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (currentScrollY / documentHeight) * 100;
      
      setScrollProgress(Math.min(scrollPercent, 100));
      
      if (currentScrollY < 100) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
        setIsMobileMenuOpen(false); // Close mobile menu when hiding nav
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  const handleApply = () => {
    navigate('/apply');
    setIsMobileMenuOpen(false);
  };

  const navbarVariants = {
    visible: { 
      y: 0, 
      transition: { 
        duration: 0.3, 
        ease: "easeOut" 
      } 
    },
    hidden: { 
      y: "-100%", 
      transition: { 
        duration: 0.3, 
        ease: "easeIn" 
      } 
    }
  };

  const mobileMenuVariants = {
    closed: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
        ease: "easeInOut"
      }
    },
    open: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  return (
    <>
      {/* Desktop Navigation */}
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 hidden md:block"
        variants={navbarVariants}
        animate={isVisible ? "visible" : "hidden"}
      >
        {/* Scroll progress bar */}
        <motion.div
          className="absolute bottom-0 left-0 h-0.5 koho-gradient"
          style={{ width: `${scrollProgress}%` }}
          initial={{ width: 0 }}
          animate={{ width: `${scrollProgress}%` }}
          transition={{ duration: 0.1 }}
        />
        
        <div className="bg-card/80 koho-blur border-b border-border/50 backdrop-blur-xl">
          <div className="container mx-auto px-6">
            <div className="flex items-center justify-between h-16">
              {/* Enhanced Brand */}
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/" className="flex items-center space-x-3 group">
                  <div className="w-10 h-10 koho-gradient rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300">
                    <span className="text-white font-bold text-sm">PSP</span>
                  </div>
                  <span className="text-xl font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                    {text?.brand}
                  </span>
                </Link>
              </motion.div>

              {/* Enhanced Navigation Links */}
              <div className="flex items-center space-x-8">
                {navigationItems?.map((item, index) => (
                  <motion.div 
                    key={item?.path}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={item?.path}
                      className={`relative font-medium transition-all duration-300 hover:scale-105 ${
                        isActiveRoute(item?.path)
                          ? 'text-primary' : 'text-muted-foreground hover:text-primary'
                      }`}
                    >
                      {item?.label}
                      {isActiveRoute(item?.path) && (
                        <motion.div
                          layoutId="activeTab"
                          className="absolute -bottom-1 left-0 right-0 h-0.5 koho-gradient rounded-full"
                          transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        />
                      )}
                    </Link>
                  </motion.div>
                ))}

                {/* Enhanced Apply Button */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handleApply}
                    className="koho-gradient hover:scale-105 koho-shadow font-semibold border-0 transition-all duration-300"
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    {text?.applyNow}
                  </Button>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Navigation */}
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 md:hidden"
        variants={navbarVariants}
        animate={isVisible ? "visible" : "hidden"}
      >
        {/* Scroll progress bar */}
        <motion.div
          className="absolute bottom-0 left-0 h-0.5 koho-gradient"
          style={{ width: `${scrollProgress}%` }}
        />
        
        <div className="bg-card/90 koho-blur border-b border-border/50">
          <div className="px-6">
            <div className="flex items-center justify-between h-16">
              {/* Mobile Brand */}
              <Link to="/" className="flex items-center space-x-2 group">
                <div className="w-8 h-8 koho-gradient rounded-lg flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300">
                  <span className="text-white font-bold text-xs">PSP</span>
                </div>
                <span className="text-xl font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                  {text?.brand}
                </span>
              </Link>

              {/* Enhanced Mobile Menu Button */}
              <motion.button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-muted-foreground hover:text-primary transition-all duration-300 hover:bg-primary/5 rounded-xl"
                whileTap={{ scale: 0.95 }}
                aria-label="Toggle menu"
              >
                <motion.div
                  animate={isMobileMenuOpen ? "open" : "closed"}
                  variants={{
                    open: { rotate: 180 },
                    closed: { rotate: 0 }
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <Icon 
                    name={isMobileMenuOpen ? "X" : "Menu"} 
                    size={24} 
                  />
                </motion.div>
              </motion.button>
            </div>
          </div>
        </div>

        {/* Enhanced Mobile Menu Overlay */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 top-16 bg-background/80 koho-blur z-30"
                onClick={() => setIsMobileMenuOpen(false)}
              />
              
              {/* Menu Content */}
              <motion.div
                variants={mobileMenuVariants}
                initial="closed"
                animate="open"
                exit="closed"
                className="fixed top-16 left-0 right-0 bg-card/95 koho-blur border-b border-border/50 shadow-xl z-40"
              >
                <div className="px-6 py-6 space-y-4">
                  {navigationItems?.map((item, index) => (
                    <motion.div
                      key={item?.path}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 + 0.1 }}
                    >
                      <Link
                        to={item?.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block py-3 px-4 rounded-xl font-medium transition-all duration-300 hover:scale-105 ${
                          isActiveRoute(item?.path)
                            ? 'bg-primary/10 text-primary koho-shadow' : 'text-muted-foreground hover:bg-primary/5 hover:text-primary'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <span>{item?.label}</span>
                          {isActiveRoute(item?.path) && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-2 h-2 koho-gradient rounded-full"
                            />
                          )}
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                  
                  <motion.div 
                    className="pt-4 border-t border-border/50"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <Button
                      variant="default"
                      fullWidth
                      onClick={handleApply}
                      className="koho-gradient hover:scale-105 koho-shadow-lg font-semibold h-12 border-0 transition-all duration-300"
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      {text?.applyNow}
                    </Button>
                  </motion.div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Spacer to prevent content from hiding behind fixed nav */}
      <div className="h-16"></div>
    </>
  );
};

export default Navigation;